import React, { useEffect, useRef, useState } from 'react';
import {
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  OutlinedInput
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useErrorMessage } from '@hooks/use-error-message.ts';
import { validateNotOnlySpaces } from '@utils/trim-form-values-utils.ts';
import ImageEditor from '@components/image-editor/image-editor.tsx';
import ColorInput from '@components/color-input/color-input.tsx';
import useOrganizationBrandingStore from '@store/organization-branding-store/organization-branding.store.ts';
import OrganizationSectionCard from '@modules/organization/components/organization-section-card/organization-section-card.tsx';
import { OrganizationBrandingReqBody } from '@modules/organization/models/organization.model.ts';
import { brandingErrorMessages } from '@modules/organization/utils/organization.utils.ts';
import useProfileStore from '@store/profile-store/profile.store.ts';

const Branding: React.FC = () => {
  const { t } = useTranslation();
  const { profileData, organizationBranding, isOrganizationBrandingLoading } =
    useProfileStore();
  const organization = profileData?.organization ?? null;
  const getErrorMessage = useErrorMessage();
  const currentPrimaryColor = useOrganizationBrandingStore(
    (state) => state.primaryColor
  );

  const {
    register,
    formState: { errors, isValid },
    handleSubmit,
    reset
  } = useForm<{ name: string }>({ mode: 'onTouched' });

  const [logoPath, setLogoPath] = useState<string>('');
  const [isUploading, setIsUploading] = useState(false);
  const [primaryColor, setPrimaryColor] = useState<string>(
    currentPrimaryColor ?? ''
  );
  const [primaryValue, setPrimaryValue] = useState<string>(
    currentPrimaryColor?.toUpperCase() ?? ''
  );
  const primaryColorInputRef = useRef<HTMLInputElement | null>(null);

  const onSubmit = ({ name }: { name: string }) => {
    const body: OrganizationBrandingReqBody = {
      image: logoPath,
      name: name.trim(),
      primaryColor
    };

    organizationBranding({ data: body, id: organization?.id });
  };

  useEffect(() => {
    if (!organization) return;

    reset({ name: organization.name || '' });
    setLogoPath(organization.image || '');

    if (organization.primaryColor) {
      setPrimaryColor(organization.primaryColor);
      setPrimaryValue(organization.primaryColor.toUpperCase());
      if (primaryColorInputRef.current) {
        primaryColorInputRef.current.value = organization.primaryColor;
      }
    }
  }, [organization, reset]);

  return (
    <OrganizationSectionCard
      title="organization_branding_dialog_content_title"
      description="organization_branding_dialog_content_text"
      className="max-w-[1000px]"
    >
      <div className="flex gap-8 flex-wrap">
        <div className="flex flex-col gap-2 justify-center">
          <ImageEditor
            imagePath={logoPath}
            onImagePathCropped={setLogoPath}
            onUploadingChange={setIsUploading}
            isRounded={false}
            cropWidth={360}
            cropHeight={140}
            imageSizePlaceholder="180x50"
            showCropPopup={false}
          />
        </div>

        <form className="flex-1 min-w-[300px] flex flex-col">
          <FormControl error={!!errors.name}>
            <FormLabel>{t('label_organization_name')}</FormLabel>
            <OutlinedInput
              {...register('name', {
                required: true,
                validate: { notOnlySpaces: validateNotOnlySpaces }
              })}
              placeholder={t('placeholder_organization_name')}
            />
            <FormHelperText>
              {errors?.name &&
                getErrorMessage({
                  errors,
                  fieldName: 'name',
                  errorMessages: brandingErrorMessages
                })}
            </FormHelperText>
          </FormControl>

          <ColorInput
            label="label_primary_color"
            color={primaryColor}
            setColor={setPrimaryColor}
            textValue={primaryValue}
            setTextValue={setPrimaryValue}
            currentRef={primaryColorInputRef}
          />
        </form>
      </div>

      <div className="flex justify-end mt-10 max-lg-xl:mt-6">
        <Button
          variant="contained"
          loading={isOrganizationBrandingLoading()}
          disabled={isUploading || !isValid || !logoPath}
          onClick={handleSubmit(onSubmit)}
        >
          {t('save')}
        </Button>
      </div>
    </OrganizationSectionCard>
  );
};

export default Branding;
